import { Component, Prop, State, h, Watch, Element, Build } from '@stencil/core';
import { debounce } from '../../../utils/helpers/debounce';
import { isVisible } from '../../../utils/helpers/is-visible';

type LayoutType = 'default' | 'centered' | 'nav-first' | 'logo-nav-first';

/**
 * Flexible navigation bar component with sticky behavior, mobile menu support, and multiple layout configurations.
 * Features responsive design with hamburger menu on mobile, focus trapping, and keyboard navigation.
 * Supports default and inverted color modes (CSS variable cascade) and customizable mobile menu width.
 *
 * @category Navigation
 * @slot area-logo - {required}, <me-logo> - Logo area, typically contains a me-logo component
 * @slot area-navbar - {required}, {multiple}, <me-button variant="ghost" tag="a">, <me-dropdown> - Navigation items for desktop view
 * @slot area-actions - {required}, <me-button variant=["primary", "secondary"]> - Action buttons area, typically for login/signup buttons
 * @slot area-navbar-mobile - {required}, {multiple}, <me-button tag="a" variant="ghost">, <me-dropdown> - Navigation items for mobile menu
 * @slot area-actions-mobile - {required}, <me-button variant=["primary", "secondary"]> - Action buttons area for mobile menu, typically for login/signup buttons
 * @example
 *   <navbar-type-1 layout="default" colorMode="default" sticky border mobileBreakpoint={960}>
 *     <me-logo slot="area-logo" href="/" src="https://upload.wikimedia.org/wikipedia/commons/4/44/Microsoft_logo.svg" alt="Company logo"></me-logo>
 *     <me-button slot="area-navbar" tag="a" variant="ghost" href="/">Product</me-button>
 *     <me-button slot="area-navbar" tag="a" variant="ghost" href="/">Solutions</me-button>
 *     <me-button slot="area-navbar" tag="a" variant="ghost" href="/">Pricing</me-button>
 *     <me-dropdown slot="area-navbar" label="Resources" href="/">
 *       <dropdown-menu-type-1 slot="dropdown-menu">
 *         <me-button variant="ghost">Help center</me-button>
 *         <me-button variant="ghost">Blog</me-button>
 *         <me-button variant="ghost">FAQ</me-button>
 *       </dropdown-menu-type-1>
 *     </me-dropdown>
 *     <me-button slot="area-actions" variant="secondary" href="/login">Contact Sales</me-button>
 *     <me-button slot="area-actions" variant="primary" href="/signup">Get started</me-button>
 *     <me-button slot="area-navbar-mobile" tag="a" variant="ghost" href="/features">Product</me-button>
 *     <me-button slot="area-navbar-mobile" tag="a" variant="ghost" href="/pricing">Solutions</me-button>
 *     <me-button slot="area-navbar-mobile" tag="a" variant="ghost" href="/about">Pricing</me-button>
 *     <me-button slot="area-actions-mobile" variant="primary" href="/login">Contact Sales</me-button>
 *     <me-button slot="area-actions-mobile" variant="secondary" href="/signup">Get started</me-button>
 *   </navbar-type-1>
 */
@Component({
  tag: 'navbar-type-1',
  styleUrl: 'index.scss',
  shadow: false,
})
export class NavbarType1 {
  @Element() host: HTMLElement;

  private desktopNavRef?: HTMLElement;
  private mobileMenuRef?: HTMLElement;

  /**
   * Additional class modifiers for styling.
   * @type {string}
   */
  @Prop() modifiers?: string;

  /**
   * Defines the layout structure of the navbar.
   * @type {string}
   * @default default
   * {enum}: ['default', 'centered', 'nav-first', 'logo-nav-first']
   */
  @Prop() layout: LayoutType = 'default';

  /**
   * Color mode for the section.
   * Controls the visual appearance via CSS variable overrides.
   * @type {string}
   * @default default
   * {enum}: ['default', 'inverted']
   */
  @Prop() colorMode: 'default' | 'inverted' = 'default';

  /**
   * Sticky behavior - navbar will stick to the top of the viewport when scrolling.
   * @type {string | boolean}
   * @default always
   * {enum}: ['always', 'hide-on-scroll', false]
   * - 'always': navbar always sticks to top
   * - 'hide-on-scroll': navbar hides when scrolling down, shows when scrolling up
   * - false: no sticky behavior
   */
  @Prop() sticky: 'always' | 'hide-on-scroll' | false = 'always';

  /**
   * Show border at the bottom of the navbar.
   * @type {boolean}
   * @default true
   */
  @Prop() border: boolean = true;

  /**
   * Width of the mobile menu container.
   * @type {string}
   * @default part
   * {enum}: ['part', 'full']
   */
  @Prop() mobileWidth: 'part' | 'full' = 'part';

  /**
   * Breakpoint width in pixels for switching to mobile view.
   * @type {number}
   * @default 768
   */
  @Prop() mobileBreakpoint: number = 768;

  /**
   * Tracks the state of the mobile menu (open or closed).
   */
  @State() isMobileMenuOpen: boolean = false;

  /**
   * Internal state for sticky modifier
   */
  @State() showSticky: boolean = true;

  /**
   * Internal state for fixed modifier (when sticky and scrolled)
   */
  @State() isFixed: boolean = false;

  /**
   * Internal state for mobile view (based on mobileBreakpoint)
   */
  @State() isMobileView: boolean = false;

  private lastScrollY: number = 0;
  private headerHeight: number = 0;
  private initialNavbarOffsetTop: number = 0; // Store initial position
  private initialOffsetCalculated: boolean = false; // Whether offset was measured while visible
  private stickyHideThreshold: number = 200;
  private stickyShowThreshold: number = 200;
  private debouncedHandleResize: () => void;
  private scrollUpStartY: number | null = null;
  private scrollDownStartY: number | null = null;

  /**
   * Calculate absolute offset from the document top by traversing the offsetParent chain.
   * Unlike this.host.offsetTop which is relative to the nearest positioned ancestor,
   * this returns the true distance from the top of the document.
   * Critical for correct banner-aware behavior when the component is nested inside
   * another positioned wrapper (e.g. sn-navbar-type-1 with position: sticky).
   */
  private getAbsoluteOffsetTop(): number {
    let offset = 0;
    let el: HTMLElement | null = this.host;
    while (el) {
      offset += el.offsetTop;
      el = el.offsetParent as HTMLElement | null;
    }
    return offset;
  }

  /**
   * Selector for all focusable elements inside the navbar
   */
  private static FOCUSABLE_SELECTOR =
    'a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), iframe, object, embed, [tabindex]:not([tabindex="-1"]), [contenteditable]';

  /**
   * Watch for changes in isMobileMenuOpen state to handle body scroll locking
   */
  @Watch('isMobileMenuOpen')
  handleMobileMenuChange(isOpen: boolean) {
    if (!Build.isBrowser) return;
    document.body.style.overflow = isOpen ? 'hidden' : '';
    if (isOpen) {
      document.addEventListener('click', this.handleOverlayClick);
      document.addEventListener('keydown', this.handleTrapFocus);
      this.updateMobileMenuPosition();
    } else {
      document.removeEventListener('click', this.handleOverlayClick);
      document.removeEventListener('keydown', this.handleTrapFocus);
    }
  }

  /**
   * Check and update mobile view state based on window width
   */
  private checkMobileView = () => {
    if (!Build.isBrowser) return;
    this.isMobileView = window.innerWidth <= this.mobileBreakpoint;
  };

  /**
   * Component lifecycle method - add event listeners
   */
  componentDidLoad() {
    if (!Build.isBrowser) return;

    this.checkMobileView();

    if (this.isMobileMenuOpen) {
      document.addEventListener('click', this.handleOverlayClick);
    }

    this.updateMobileMenuPosition();
    window.addEventListener('resize', this.updateMobileMenuPosition);

    // Apply sticky positioning to host element (custom element wrapper).
    // This prevents the "tight wrapper" problem: position: sticky on the inner
    // <header> doesn't work because the host wraps it tightly (same height).
    if (this.sticky) {
      this.host.classList.add('is-sticky-host');
    }

    // sticky scroll logic for hide-on-scroll mode
    if (this.sticky === 'hide-on-scroll') {
      this.lastScrollY = window.scrollY;
      this.headerHeight = this.desktopNavRef ? this.desktopNavRef.offsetHeight : 0;
      window.addEventListener('scroll', this.handleScroll, { passive: true });
    }

    // Track scroll for is-fixed class (banner-aware box-shadow)
    if (this.sticky) {
      // Store initial navbar position (before it becomes sticky).
      // This accounts for any elements above the navbar (banners, etc.)
      // Note: if the element is hidden (e.g. inside sn-navbar-type-1 with display:none),
      // offsetHeight is 0 and the offset will be recalculated on first scroll when visible.
      this.initialNavbarOffsetTop = this.getAbsoluteOffsetTop();
      this.initialOffsetCalculated = this.host.offsetHeight > 0;
      window.addEventListener('scroll', this.handleFixedState, { passive: true });
      this.handleFixedState(); // Check initial scroll state
    }

    // Close mobile menu on resize to desktop
    this.debouncedHandleResize = debounce(() => {
      // Update mobile view state
      this.checkMobileView();

      if (window.innerWidth > this.mobileBreakpoint && this.isMobileMenuOpen) {
        this.isMobileMenuOpen = false;
      }
      // Recalculate header height on resize
      this.headerHeight = this.desktopNavRef ? this.desktopNavRef.offsetHeight : 0;

      // Recalculate initial offset on resize
      if (this.sticky) {
        this.initialNavbarOffsetTop = this.getAbsoluteOffsetTop();
        this.initialOffsetCalculated = this.host.offsetHeight > 0;
      }
    }, 100);
    window.addEventListener('resize', this.debouncedHandleResize);

    // Add role="menu" and role="menuitem" for area-navbar
    this.setMenuRoles();

    // Keep nested me-dropdowns in sync with the navbar's mobile breakpoint so
    // they switch from hover to click at the same width the navbar collapses to
    // its burger menu (otherwise they'd stay in desktop/hover mode between the
    // 768px CSS breakpoint and this navbar's mobileBreakpoint, e.g. 769–960px).
    this.syncDropdownBreakpoint();
  }

  componentDidUpdate() {
    this.setMenuRoles();
    this.syncDropdownBreakpoint();
  }

  /**
   * Propagate this navbar's `mobileBreakpoint` to every nested `me-dropdown`
   * (desktop nav and mobile menu), so their hover-vs-click behavior matches the
   * navbar's collapse breakpoint.
   */
  private syncDropdownBreakpoint() {
    if (!Build.isBrowser) return;
    this.host.querySelectorAll('me-dropdown').forEach(dropdown => {
      dropdown.setAttribute('mobile-breakpoint', String(this.mobileBreakpoint));
    });
  }

  /**
   * Adds role="menu" to area-navbar and role="menuitem" to all items
   */
  private setMenuRoles() {
    const navColumn = this.host.querySelector('.navbar__nav-column');
    if (navColumn) {
      navColumn.setAttribute('role', 'menu');
      // Direct child elements (me-button, me-dropdown)
      Array.from(navColumn.children).forEach(child => {
        if (child.tagName.toLowerCase() === 'me-button' || child.tagName.toLowerCase() === 'me-dropdown') {
          child.setAttribute('role', 'menuitem');
        }
      });
    }
  }

  /**
   * Component lifecycle method - cleanup
   */
  disconnectedCallback() {
    document.removeEventListener('click', this.handleOverlayClick);
    window.removeEventListener('resize', this.updateMobileMenuPosition);
    if (this.debouncedHandleResize) {
      window.removeEventListener('resize', this.debouncedHandleResize);
    }
    document.removeEventListener('keydown', this.handleTrapFocus);

    // Cleanup sticky scroll listeners, host classes and body classes
    if (this.sticky) {
      window.removeEventListener('scroll', this.handleFixedState);
      this.host.classList.remove('is-fixed');
      document.body.classList.remove('navbar-fixed');
    }
    if (this.sticky === 'hide-on-scroll') {
      window.removeEventListener('scroll', this.handleScroll);
      document.body.classList.remove('navbar-hidden');
    }
  }

  /**
   * Update mobile menu position based on desktop navbar height
   */
  private updateMobileMenuPosition = () => {
    if (this.desktopNavRef && this.mobileMenuRef) {
      // Avoid 1px visual gap from offsetHeight rounding up fractional height.
      const height = Math.floor(this.desktopNavRef.getBoundingClientRect().height);
      const viewportHeight = window.visualViewport?.height || window.innerHeight;
      const availableHeight = Math.max(0, Math.floor(viewportHeight - height));
      this.mobileMenuRef.style.top = `${height}px`;
      this.mobileMenuRef.style.height = `${availableHeight}px`;
    }
  };

  /**
   * Handle clicks on the overlay to close the mobile menu
   */
  private handleOverlayClick = (event: MouseEvent) => {
    if (!this.isMobileMenuOpen) return;

    const target = event.target as HTMLElement;
    // Scope these lookups to THIS navbar instance. A global `getElementById`/
    // `querySelector` would resolve to the first match in the document, which is
    // the SSR placeholder's embedded navbar (it renders its own `#mobile-menu`
    // and toggle earlier in the DOM). That made this handler treat every burger
    // click as an "outside" click and immediately re-close the menu.
    const mobileMenu = this.mobileMenuRef;
    const mobileToggle = this.host.querySelector('.navbar-type-1__mobile-toggle');

    if (mobileMenu && mobileToggle && !mobileMenu.contains(target) && !mobileToggle.contains(target)) {
      this.toggleMobileMenu();
      event.preventDefault();
      event.stopPropagation();
    }
  };

  /**
   * Toggle mobile menu state
   */
  toggleMobileMenu() {
    this.isMobileMenuOpen = !this.isMobileMenuOpen;
  }

  private handleScroll = () => {
    if (!Build.isBrowser || this.sticky !== 'hide-on-scroll') return;
    const currentScrollY = window.scrollY;
    if (this.isMobileView) return;

    if (currentScrollY <= this.headerHeight + this.stickyHideThreshold) {
      this.showSticky = true;
      this.host.style.top = '0';
      this.scrollUpStartY = null;
      this.scrollDownStartY = null;
    } else if (currentScrollY > this.lastScrollY) {
      // Scroll down
      if (this.scrollDownStartY === null) {
        this.scrollDownStartY = this.lastScrollY;
      }
      if (currentScrollY - this.scrollDownStartY > this.stickyHideThreshold) {
        this.showSticky = false;
        this.host.style.top = `-${this.headerHeight}px`;
        document.body.classList.add('navbar-hidden');
      }
      this.scrollUpStartY = null;
    } else if (currentScrollY < this.lastScrollY) {
      // Scroll up
      if (this.scrollUpStartY === null) {
        this.scrollUpStartY = this.lastScrollY;
      }
      if (this.scrollUpStartY - currentScrollY > this.stickyShowThreshold) {
        this.showSticky = true;
        this.host.style.top = '0';
        document.body.classList.remove('navbar-hidden');
      }
      this.scrollDownStartY = null;
    }
    this.lastScrollY = currentScrollY;
  };

  /**
   * Handle is-fixed state based on scroll position.
   * Arrow function for stable reference (addEventListener/removeEventListener).
   */
  private handleFixedState = () => {
    if (!Build.isBrowser || !this.sticky) return;

    // Recalculate offset when the element becomes visible for the first time.
    // This handles the case where componentDidLoad fires while the element is hidden
    // (e.g. inside sn-navbar-type-1 with display:none during loading state).
    if (!this.initialOffsetCalculated && this.host.offsetHeight > 0) {
      this.initialNavbarOffsetTop = this.getAbsoluteOffsetTop();
      this.initialOffsetCalculated = true;
    }

    // Add is-fixed class only when:
    // 1. User has scrolled (scrollY > 0)
    // 2. Scroll position has passed the initial navbar position
    // This accounts for any elements above the navbar (banners, etc.)
    const shouldBeFixed = window.scrollY > 0 && window.scrollY >= this.initialNavbarOffsetTop;

    if (shouldBeFixed === this.isFixed) return;

    // Update isFixed state (triggers re-render → is-fixed class on inner <header>)
    this.isFixed = shouldBeFixed;

    // Also toggle is-fixed on the HOST element for box-shadow.
    // Box-shadow must be on the outermost sticky element to be visually correct.
    if (shouldBeFixed) {
      this.host.classList.add('is-fixed');
    } else {
      this.host.classList.remove('is-fixed');
    }

    // Add/remove navbar-fixed class to body for 'always' sticky mode
    // Similar to navbar-hidden for 'hide-on-scroll' mode
    if (this.sticky === 'always') {
      if (shouldBeFixed) {
        document.body.classList.add('navbar-fixed');
      } else {
        document.body.classList.remove('navbar-fixed');
      }
    }
  };

  /**
   * Get all focusable elements inside the given container
   */
  private getFocusableElements(container: HTMLElement): HTMLElement[] {
    return Array.from(container.querySelectorAll<HTMLElement>(NavbarType1.FOCUSABLE_SELECTOR)).filter(
      el => !el.hasAttribute('disabled') && !el.getAttribute('aria-hidden') && isVisible(el),
    );
  }

  /**
   * Focus trap: prevents focus from leaving the navbar when the mobile menu is open
   */
  private handleTrapFocus = (e: KeyboardEvent) => {
    if (!this.isMobileMenuOpen) return;
    if (e.key !== 'Tab') return;
    const focusableEls = this.getFocusableElements(this.host);
    if (focusableEls.length === 0) return;
    const firstEl = focusableEls[0];
    const lastEl = focusableEls[focusableEls.length - 1];
    if (e.shiftKey) {
      if (document.activeElement === firstEl) {
        e.preventDefault();
        lastEl.focus();
      }
    } else {
      if (document.activeElement === lastEl) {
        e.preventDefault();
        firstEl.focus();
      }
    }
  };

  render() {
    const mobileWidthClass = this.mobileWidth === 'full' ? 'navbar-type-1--mobile-full-width' : '';

    return (
      <header
        class={`navbar-type-1 navbar-type-1--${this.layout} ${this.sticky && this.showSticky ? 'navbar-type-1--sticky' : ''} ${this.isFixed ? 'is-fixed' : ''} ${this.border ? 'navbar-type-1--with-border' : ''} ${mobileWidthClass} ${this.isMobileView ? 'is-mobile-view' : ''} ${this.modifiers || ''}`}
        data-color-mode={this.colorMode !== 'default' ? this.colorMode : undefined}
      >
        <nav class="navbar-type-1__container navbar-type-1__container--desktop" role="navigation" aria-label="Main navigation" ref={el => (this.desktopNavRef = el as HTMLElement)}>
          <div class="navbar-type-1__column navbar-type-1__logo-column">
            <slot name="area-logo"></slot>
          </div>
          <div class="navbar-type-1__column navbar-type-1__nav-column">
            <slot name="area-navbar"></slot>
          </div>
          <div class="navbar-type-1__column navbar-type-1__actions-column">
            <slot name="area-actions"></slot>
          </div>
          <div
            class={{
              'navbar-type-1__mobile-toggle': true,
              'is-active': this.isMobileMenuOpen,
            }}
            role="button"
            tabIndex={0}
            aria-label={this.isMobileMenuOpen ? 'Close menu' : 'Main menu'}
            onClick={() => this.toggleMobileMenu()}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                this.toggleMobileMenu();
              }
            }}
            aria-expanded={this.isMobileMenuOpen ? 'true' : 'false'}
            aria-controls="mobile-menu"
          >
            <div class="navbar-type-1__mobile-toggle__line"></div>
            <div class="navbar-type-1__mobile-toggle__line"></div>
            <div class="navbar-type-1__mobile-toggle__line"></div>
          </div>
        </nav>
        <nav
          id="mobile-menu"
          class={{
            'navbar-type-1__container': true,
            'navbar-type-1__container--mobile': true,
            'is-open': this.isMobileMenuOpen,
          }}
          role="navigation"
          aria-label="Mobile navigation"
          aria-hidden={!this.isMobileMenuOpen ? 'true' : 'false'}
          ref={el => (this.mobileMenuRef = el as HTMLElement)}
        >
          <div class="navbar-type-1__mobile-content">
            <slot name="area-navbar-mobile"></slot>
            <div class="navbar-type-1__mobile-actions">
              <slot name="area-actions-mobile"></slot>
            </div>
          </div>
        </nav>
        <slot name="area-search"></slot>
      </header>
    );
  }
}
